// Admin price settings: the exchange rate, each product's free monthly
// allowance and its volume bands. Any admin can look; a super admin can
// change or restore the defaults. The gateway applies changes within a minute.
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { getErrorMessage } from '@/lib/api';
import { adminPricingApi, PRODUCT_LABELS, toNaira, type PricingSettings } from '@/lib/pricing';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Notice } from '@/components/ui/Notice';
import { Skeleton } from '@/components/ui/Skeleton';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table';
import { fieldControlSm, fieldLabel } from '@/components/ui/fieldStyles';

type BandDraft = { upTo: string; usd: string; ngn: string };
type Draft = { rate: string; products: Record<string, { free: string; bands: BandDraft[] }> };

const SERVICE_LABELS: Record<string, string> = {
  route: 'routing', render: 'map tiles', geocode: 'search', reverse: 'reverse search', convert: 'address codes',
  overpass: 'nearby features', transit: 'transit', rail: 'rail',
};

const toDraft = (s: PricingSettings): Draft => ({
  rate: String(s.usd_ngn),
  products: Object.fromEntries(Object.entries(s.products).map(([id, p]) => [id, {
    free: String(p.freePerMonth),
    bands: p.bands.map(b => ({ upTo: b.upTo == null ? '' : String(b.upTo), usd: String(b.usdPer1000), ngn: b.ngnPer1000 == null ? '' : String(b.ngnPer1000) })),
  }])),
});

const num = (v: string) => (v.trim() === '' ? NaN : Number(v.replace(/,/g, '')));

const fromDraft = (d: Draft): Pick<PricingSettings, 'usd_ngn' | 'products'> => ({
  usd_ngn: num(d.rate),
  products: Object.fromEntries(Object.entries(d.products).map(([id, p]) => [id, {
    freePerMonth: num(p.free),
    bands: p.bands.map((b, i) => ({
      upTo: i === p.bands.length - 1 ? null : num(b.upTo),
      usdPer1000: num(b.usd),
      ngnPer1000: b.ngn.trim() === '' ? null : num(b.ngn),
    })),
  }])),
});

const fmt = (n: number) => (Number.isFinite(n) ? n.toLocaleString('en-GB') : '…');
const inputCls = cn(fieldControlSm, 'font-mono tabular-nums');

const PricingPage: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-pricing'], queryFn: adminPricingApi.get });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirm, setConfirm] = useState<'save' | 'reset' | null>(null);

  useEffect(() => { if (q.data && !draft) setDraft(toDraft(q.data.settings)); }, [q.data, draft]);

  const saved = useMemo(() => (q.data ? JSON.stringify(fromDraft(toDraft(q.data.settings))) : ''), [q.data]);
  const dirty = !!draft && JSON.stringify(fromDraft(draft)) !== saved;
  const canEdit = !!q.data?.can_edit;
  const rate = draft ? num(draft.rate) : NaN;

  const onDone = (message: string) => {
    setConfirm(null);
    setDraft(null);
    void qc.invalidateQueries({ queryKey: ['admin-pricing'] });
    void qc.invalidateQueries({ queryKey: ['pricing'] });
    toast.success(message);
  };
  const save = useMutation({
    mutationFn: () => adminPricingApi.save(fromDraft(draft!)),
    onSuccess: () => onDone('Prices saved. The gateway charges them within a minute.'),
    onError: e => { setConfirm(null); toast.error(getErrorMessage(e)); },
  });
  const reset = useMutation({
    mutationFn: () => adminPricingApi.reset(),
    onSuccess: () => onDone('Default prices restored'),
    onError: e => { setConfirm(null); toast.error(getErrorMessage(e)); },
  });

  if (q.isLoading || (q.data && !draft)) {
    return (
      <div className="mx-auto max-w-5xl space-y-6" aria-hidden="true">
        <div><Skeleton className="h-8 w-40" /><Skeleton className="mt-2 h-4 w-96 max-w-full" /></div>
        <Skeleton className="h-24 rounded-2xl" />
        {[0, 1].map(i => <Skeleton key={i} className="h-64 rounded-2xl" />)}
      </div>
    );
  }
  if (q.isError || !q.data || !draft) return <Notice tone="error">Could not load prices. Try again shortly.</Notice>;

  const { settings, is_default, services } = q.data;
  const productsOf = Object.entries(services).reduce<Record<string, string[]>>((acc, [svc, ids]) => {
    for (const id of ids) (acc[id] ??= []).push(SERVICE_LABELS[svc] ?? svc);
    return acc;
  }, {});
  const setProduct = (id: string, f: (p: Draft['products'][string]) => Draft['products'][string]) =>
    setDraft(d => d && ({ ...d, products: { ...d.products, [id]: f(d.products[id]!) } }));
  const setBand = (id: string, i: number, patch: Partial<BandDraft>) =>
    setProduct(id, p => ({ ...p, bands: p.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-24">
      <PageHeader
        title="Pricing"
        description={<>
          What customers pay per request, tile or matrix element. Each product follows Mapbox's US$ price list, converted at the
          exchange rate below and rounded down to the nearest ₦100 per 1,000, unless you set a naira price for a band.
          One credit is ₦0.10. Services without a product here keep the flat credits set on the Services page.
        </>}
      />
      <Notice tone={canEdit ? 'neutral' : 'info'} role="status">
        {is_default
          ? 'Using the default prices built into the gateway.'
          : `Changed by ${settings.updated_by ?? 'an admin'}${settings.updated_at ? ` on ${new Date(settings.updated_at * 1000).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}. Prices shown to customers as of ${settings.as_of}.`}
        {!canEdit && ' Only a super admin can change prices.'}
      </Notice>

      <Panel>
        <label htmlFor="usd-ngn" className={fieldLabel}>Exchange rate (₦ per US$)</label>
        <div className="flex flex-wrap items-center gap-3">
          <input id="usd-ngn" inputMode="decimal" className={cn(inputCls, 'max-w-[12rem]')} disabled={!canEdit}
            value={draft.rate} onChange={e => setDraft(d => d && ({ ...d, rate: e.target.value }))} />
          <span className="text-sm text-muted-foreground">Default <span className="font-mono tabular-nums text-foreground">{fmt(q.data.defaults.usd_ngn)}</span> (CBN official rate, {q.data.defaults.as_of})</span>
        </div>
      </Panel>

      {Object.entries(draft.products).map(([id, p]) => {
        const free = num(p.free);
        return (
          <Panel
            key={id}
            title={PRODUCT_LABELS[id] ?? id}
            action={
              <p className="text-right text-xs text-muted-foreground">
                {q.data.preview.products.find(x => x.id === id)?.mapbox} · per {q.data.preview.products.find(x => x.id === id)?.unit}
                {productsOf[id] ? ` · charged on ${[...new Set(productsOf[id])].join(', ')}` : ''}
              </p>
            }
            bodyClassName="px-0 pb-4"
          >
            <div className="flex flex-wrap items-center gap-3 px-5 pb-4">
              <label htmlFor={`${id}-free`} className="text-sm text-muted-foreground">Free each month</label>
              <input id={`${id}-free`} inputMode="numeric" className={cn(inputCls, 'max-w-[10rem]')} disabled={!canEdit}
                value={p.free} onChange={e => setProduct(id, x => ({ ...x, free: e.target.value }))} />
            </div>
            <div className="border-t border-border">
              <Table minWidth={680}>
                <THead>
                  <tr>
                    <Th>From</Th><Th>Up to</Th>
                    <Th>US$ per 1,000</Th><Th>₦ per 1,000</Th>
                    <Th align="right">Credits each</Th><Th><span className="sr-only">Actions</span></Th>
                  </tr>
                </THead>
                <TBody>
                  {p.bands.map((b, i) => {
                    const last = i === p.bands.length - 1;
                    const from = (i === 0 ? free : num(p.bands[i - 1]!.upTo)) + 1;
                    const converted = toNaira(num(b.usd), rate);
                    const ngn = b.ngn.trim() === '' ? converted : num(b.ngn);
                    return (
                      <Tr key={i}>
                        <Td mono muted className="py-2">{fmt(from)}</Td>
                        <Td className="py-2">
                          {last ? <span className="text-muted-foreground">and above</span> : (
                            <input aria-label={`${id} band ${i + 1} up to`} inputMode="numeric" className={inputCls} disabled={!canEdit}
                              value={b.upTo} onChange={e => setBand(id, i, { upTo: e.target.value })} />
                          )}
                        </Td>
                        <Td className="py-2">
                          <input aria-label={`${id} band ${i + 1} US$ per 1,000`} inputMode="decimal" className={inputCls} disabled={!canEdit}
                            value={b.usd} onChange={e => setBand(id, i, { usd: e.target.value })} />
                        </Td>
                        <Td className="py-2">
                          <input aria-label={`${id} band ${i + 1} naira per 1,000`} inputMode="numeric" className={inputCls} disabled={!canEdit}
                            placeholder={Number.isFinite(converted) ? `${fmt(converted)} (converted)` : ''}
                            value={b.ngn} onChange={e => setBand(id, i, { ngn: e.target.value })} />
                        </Td>
                        <Td align="right" mono className="py-2">{Number.isFinite(ngn) ? fmt(Math.round(ngn / 100)) : '…'}</Td>
                        <Td align="right" className="py-2">
                          {canEdit && p.bands.length > 1 && (
                            <Button size="sm" variant="ghost" aria-label={`Remove ${id} band ${i + 1}`}
                              onClick={() => setProduct(id, x => {
                                const bands = x.bands.filter((_, j) => j !== i);
                                return { ...x, bands: bands.map((bb, j) => (j === bands.length - 1 ? { ...bb, upTo: '' } : bb)) };
                              })}>Remove</Button>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </div>
            {canEdit && p.bands.length < 6 && (
              <Button className="ml-5 mt-3" size="sm" variant="outline"
                onClick={() => setProduct(id, x => {
                  // The old top band gets an upper limit; the new one becomes the open top band.
                  const prev = x.bands[x.bands.length - 2]?.upTo;
                  const start = num(prev ?? x.free);
                  const bands = x.bands.map((bb, j) => (j === x.bands.length - 1 ? { ...bb, upTo: String((Number.isFinite(start) ? start : 0) + 1_000_000) } : bb));
                  const top = x.bands[x.bands.length - 1]!;
                  return { ...x, bands: [...bands, { upTo: '', usd: top.usd, ngn: top.ngn }] };
                })}>Add band</Button>
            )}
          </Panel>
        );
      })}

      {canEdit && (
        <div className="sticky bottom-4 flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-border bg-surface-raised p-3 shadow-lg shadow-black/10">
          <span className="mr-auto text-sm text-muted-foreground">{dirty ? 'Unsaved changes' : 'No changes'}</span>
          {!is_default && <Button variant="outline" onClick={() => setConfirm('reset')}>Restore defaults</Button>}
          <Button variant="outline" disabled={!dirty} onClick={() => setDraft(toDraft(settings))}>Discard</Button>
          <Button disabled={!dirty} onClick={() => setConfirm('save')}>Save prices</Button>
        </div>
      )}

      <Modal isOpen={confirm === 'save'} onClose={() => setConfirm(null)} title="Save new prices">
        <p className="text-sm text-muted-foreground">
          Every customer is charged the new prices within a minute, and the public price list changes with them.
          Usage already counted this month keeps counting towards the free allowance and bands.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button loading={save.isLoading} onClick={() => save.mutate()}>Save prices</Button>
        </div>
      </Modal>
      <Modal isOpen={confirm === 'reset'} onClose={() => setConfirm(null)} title="Restore default prices">
        <p className="text-sm text-muted-foreground">Your changes are removed and the prices built into the gateway apply again, within a minute.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="danger" loading={reset.isLoading} onClick={() => reset.mutate()}>Restore defaults</Button>
        </div>
      </Modal>
    </div>
  );
};

export default PricingPage;
